import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { DateTimeUtils } from '@mews-framework/datetime';
import { useDateFieldTransform } from '@mews-framework/form';
import { useDateTimeLocalizationInfo, useTimezone } from '@mews-framework/globalization';
import { atLeast, BreakpointRange, useBreakpointRange } from '@optimus-web/breakpoints';
import {
    Container,
    Divider,
    DropdownItem,
    getBreakpointRangeValueFallback,
    Icon,
    IconName,
    OpenableOverlay,
    StructuredDate,
    useClickAway,
    withFocusTrap,
} from '@optimus-web/core';
import { isOptimusTextKey, OptimusTextKey, useLocalization } from '@optimus-web/localization';
import { FilterInputTrigger } from '../FilterInput';
import { datePeriods, dateRangePeriodDropdownOptions } from './constants';
import { DateRangeCalendar } from './DateRangeCalendar';
import { CustomDateRange, DatePeriod, DateRangePickerDropdownProps } from './types';
import { getTransformedDateRangeOutput, useGetDefaultCustomRangeState, useGetDefaultInputValue } from './utils';

const DateRangeDropdownComponent = ({
    allowOneDateRange,
    calendarService,
    dateRange,
    mode = 'iso',
    onChange,
    prefix,
}: DateRangePickerDropdownProps) => {
    const breakpointRange = useBreakpointRange();
    const timezone = useTimezone();
    const transform = useDateFieldTransform(mode);
    const localizationInfo = useDateTimeLocalizationInfo();
    const { t } = useLocalization();
    const numberOfMonths = atLeast(BreakpointRange.Desktop)(getBreakpointRangeValueFallback(breakpointRange)) ? 2 : 1;
    const { datePeriod } = dateRange;

    const chipRef = useRef<HTMLButtonElement>(null);
    const menuRef = useRef(null);
    const { registerClickAway, deregisterClickAway } = useClickAway();

    const defaultDatePeriod = useGetDefaultInputValue({
        dateRange,
        mode,
        transform,
    });

    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [isCustomRangeDialogOpen, setIsCustomRangeDialogOpen] = useState(false);
    const [inputValue, setInputValue] = useState<OptimusTextKey | string>(defaultDatePeriod);
    const [prevDatePeriod, setPrevDatePeriod] = useState(dateRange.datePeriod);
    const defaultCustomRangeState = useGetDefaultCustomRangeState(mode, dateRange);
    const [customRange, setCustomRange] = useState<CustomDateRange>(defaultCustomRangeState);

    if (dateRange.datePeriod !== DatePeriod.Custom && dateRange.datePeriod !== prevDatePeriod) {
        setInputValue(datePeriods[dateRange.datePeriod].label);
        setPrevDatePeriod(dateRange.datePeriod);
    }

    const handleCloseDropdown = useCallback(() => {
        setIsDropdownOpen(false);
        deregisterClickAway();
    }, [deregisterClickAway]);

    useEffect(() => {
        if (isDropdownOpen) {
            registerClickAway({
                handler: handleCloseDropdown,
                roots: [() => menuRef.current],
            });
        }
    }, [handleCloseDropdown, isDropdownOpen, registerClickAway]);

    const handleCancelCustomDateRange = () => {
        setIsCustomRangeDialogOpen(false);
    };

    const handleSelectChange = (newDatePeriod: DatePeriod) => {
        if (newDatePeriod === DatePeriod.Custom) {
            handleCloseDropdown();
            setIsCustomRangeDialogOpen(true);
            return;
        }

        setInputValue(datePeriods[newDatePeriod].label);
        onChange({
            datePeriod: newDatePeriod,
        });
        handleCloseDropdown();
    };

    const handleCustomRangeChange = (startDate: StructuredDate, endDate: StructuredDate) => {
        setCustomRange({ endDate, startDate });
    };

    const handleApply = () => {
        if (!customRange.startDate || !customRange.endDate) {
            return null;
        }

        const endDateIsoString = DateTimeUtils.structuredDateToISOString(customRange.endDate);
        const startDateIsoString = DateTimeUtils.structuredDateToISOString(customRange.startDate);
        const endDateString = DateTimeUtils.dateIsoStringToDateString(endDateIsoString, localizationInfo);
        const startDateString = DateTimeUtils.dateIsoStringToDateString(startDateIsoString, localizationInfo);

        const transformedEndDateInput = transform?.input ? (transform.input(endDateString) as string) : endDateString;
        const transformedStartDateInput = transform?.input
            ? (transform.input(startDateString) as string)
            : startDateString;
        const inputString = getTransformedDateRangeOutput({
            endDate: endDateIsoString,
            startDate: startDateIsoString,
            localizationInfo,
            mode: 'iso',
            timezone,
            transform,
        });

        setInputValue(inputString);
        onChange({
            datePeriod: DatePeriod.Custom,
            endDate: transformedEndDateInput,
            startDate: transformedStartDateInput,
        });
        setIsCustomRangeDialogOpen(false);
    };

    return (
        <Container>
            <FilterInputTrigger
                dataTestId="Period-select"
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                prefix={prefix}
                ref={chipRef}
                showDropdownIcon={false}
                suffix={<Icon value={IconName.Calendar} size="small" />}
                value={isOptimusTextKey(inputValue) ? String(t(inputValue)) : inputValue}
            />
            <OpenableOverlay
                anchorElement={chipRef.current}
                innerRef={menuRef}
                opened={isDropdownOpen}
                placement="bottom-end"
                ref={menuRef}
            >
                <Container minWidth="180px" maxWidth="320px">
                    {dateRangePeriodDropdownOptions.map(({ label, value, withDivider }) => (
                        <Fragment key={value}>
                            <DropdownItem
                                data-test-id="Period-select-item"
                                label={t(label)}
                                onClick={() => handleSelectChange(value)}
                                rightDetail={
                                    value === DatePeriod.Custom ? (
                                        <Icon value={IconName.ChevronRight} size="small" variant="primary" />
                                    ) : null
                                }
                                selected={datePeriod === value}
                                value={value}
                            />
                            {withDivider ? <Divider /> : null}
                        </Fragment>
                    ))}
                </Container>
            </OpenableOverlay>
            <DateRangeCalendar
                allowOneDateRange={allowOneDateRange}
                calendarService={calendarService}
                endDate={customRange.endDate}
                isDialogOpen={isCustomRangeDialogOpen}
                numberOfMonths={numberOfMonths}
                onApply={handleApply}
                onCancel={handleCancelCustomDateRange}
                onRangeChange={handleCustomRangeChange}
                startDate={customRange.startDate}
            />
        </Container>
    );
};

export const DateRangeDropdown = withFocusTrap(DateRangeDropdownComponent);
